import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { AppDispatch } from "../../store/store";
import { selectUploads, reduxAddUpload } from "../../store/dreamworldSlice";
import uploadImageToFirebase from "../../utils/uploadImageToFirebase";
import { v4 as uuidv4 } from "uuid";
import { Timestamp } from "firebase/firestore";
import { selectUserGlobalData } from "../../store/dreamworldSlice";
import { LibraryItem } from "../../types/library";
import dreamWorldProvider from "../../DreamWorldProvider";

interface UploadsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onImageSelect: (image: LibraryItem) => void;
}

const UploadsPanel: React.FC<UploadsPanelProps> = ({
  isOpen,
  onClose,
  onImageSelect,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const uploads = useSelector(selectUploads);
  const userGlobalData = useSelector(selectUserGlobalData);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && userGlobalData) {
      const file = e.target.files[0];
      setIsUploading(true);
      try {
        const downloadUrl = await uploadImageToFirebase(file, "postsImages");
        const docId = uuidv4();
        const newUpload: LibraryItem = {
          id: docId,
          ownerId: userGlobalData.user.uid,
          type: "Image",
          fullUrl: downloadUrl,
          thumbnailUrl: downloadUrl,
          prompt: "",
          isFavorite: false,
          views: 0,
          likes: 0,
          tags: [],
          size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
          createdAt: Timestamp.now(),
          isPublished: false,
          title: file.name,
        };
        await dreamWorldProvider.saveUploadItem(newUpload);
        dispatch(reduxAddUpload(newUpload));
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setIsUploading(false);
        // Reset the input value to allow re-uploading the same file
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleUploadClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  // Create portal content
  const portalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{ zIndex: 999999 }}
            onClick={handleClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-gray-900/95 backdrop-blur-xl border-l border-white/10 p-6 overflow-y-auto"
            style={{ zIndex: 1000000 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Your Uploads</h2>
              <button
                onClick={handleClose}
                disabled={isUploading}
                className={`p-2 rounded-lg transition-colors ${
                  isUploading
                    ? "text-white/30 cursor-not-allowed"
                    : "hover:bg-white/10 text-white/60 hover:text-white"
                }`}
              >
                <X size={20} />
              </button>
            </div>

            {/* Upload Button */}
            <motion.button
              onClick={handleUploadClick}
              disabled={isUploading}
              className={`flex items-center justify-center w-full gap-2 p-3 rounded-lg mb-4 transition-all ${
                isUploading
                  ? "bg-purple-600/50 cursor-not-allowed"
                  : "bg-purple-600 hover:bg-purple-700"
              } text-white`}
              whileHover={!isUploading ? { scale: 1.05 } : {}}
              whileTap={!isUploading ? { scale: 0.95 } : {}}
            >
              {isUploading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <UploadCloud size={20} />
                  <span>Upload Image</span>
                </>
              )}
            </motion.button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept="image/*"
              disabled={isUploading}
            />

            {/* Loading State for Grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 size={32} className="animate-spin text-purple-400" />
                  <span className="text-white/60">Loading uploads...</span>
                </div>
              </div>
            ) : (
              /* Grid */
              <div className="grid grid-cols-2 gap-4">
                {uploads.map((item) => (
                  <motion.div
                    key={item.id}
                    className="relative group rounded-lg overflow-hidden cursor-pointer bg-gray-800/50"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => onImageSelect(item)}
                  >
                    <div className="aspect-square relative">
                      <img
                        src={item.thumbnailUrl}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <div className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm">
                            Select
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {/* Empty State */}
                {uploads.length === 0 && !isLoading && (
                  <div className="col-span-2 flex flex-col items-center justify-center py-12 text-center">
                    <UploadCloud size={48} className="text-white/30 mb-4" />
                    <h3 className="text-white/60 font-medium mb-2">
                      No uploads yet
                    </h3>
                    <p className="text-white/40 text-sm">
                      Upload your first image to get started
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Upload Progress Overlay */}
            <AnimatePresence>
              {isUploading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center"
                  style={{ zIndex: 1000001 }}
                >
                  <div className="bg-gray-800/90 rounded-lg p-6 flex flex-col items-center gap-4 mx-6">
                    <Loader2
                      size={40}
                      className="animate-spin text-purple-400"
                    />
                    <div className="text-center">
                      <h3 className="text-white font-medium mb-1">
                        Uploading Image
                      </h3>
                      <p className="text-white/60 text-sm">
                        Please wait while we process your upload...
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // Render using portal to document.body
  return typeof document !== "undefined"
    ? createPortal(portalContent, document.body)
    : null;
};

export default UploadsPanel;
